// src/components/Footer.js
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaArrowRight,
  FaEnvelopeOpenText,
  FaFacebookF,
  FaGlobe,
  FaLinkedinIn,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaShieldAlt,
  FaTwitter,
} from "react-icons/fa";
import NewsletterForm from "./NewsletterForm";

function Footer() {
  const year = new Date().getFullYear();

  const socialLinks = useMemo(
    () => [
      { href: "https://facebook.com", label: "Facebook", icon: <FaFacebookF aria-hidden="true" /> },
      { href: "https://linkedin.com", label: "LinkedIn", icon: <FaLinkedinIn aria-hidden="true" /> },
      { href: "https://twitter.com", label: "Twitter", icon: <FaTwitter aria-hidden="true" /> },
      { href: "mailto:info@ibgambia.com", label: "Email IBG", icon: <FaEnvelopeOpenText aria-hidden="true" /> },
    ],
    []
  );

  const footerNav = useMemo(
    () => [
      { to: "/about", label: "About" },
      { to: "/programs", label: "Programs" },
      { to: "/admissions", label: "Admissions" },
      { to: "/achievements", label: "Achievements" },
      { to: "/partners", label: "Partners" },
      { to: "/contact", label: "Contact" },
    ],
    []
  );

  return (
    <footer
      role="contentinfo"
      aria-label="Footer"
      style={{
        fontFamily: "'Inter', 'Segoe UI', Roboto, system-ui, -apple-system, sans-serif",
        background: "var(--ibg-ink)",
        color: "var(--ibg-white)",
        marginTop: "auto",
        overflow: "hidden",
      }}
    >
      {/* Decorative top glow */}
      <div
        aria-hidden="true"
        style={{
          height: 1,
          background:
            "linear-gradient(90deg, rgba(13,110,253,0) 0%, rgba(13,110,253,0.55) 35%, rgba(102,178,255,0.35) 65%, rgba(13,110,253,0) 100%)",
        }}
      />

      <div
        aria-hidden="true"
        style={{
          position: "relative",
          height: 0,
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: "-180px -120px auto -120px",
            height: 280,
            background:
              "radial-gradient(circle at 30% 30%, rgba(13,110,253,0.22), transparent 55%), radial-gradient(circle at 70% 40%, rgba(102,178,255,0.14), transparent 60%)",
            filter: "blur(10px)",
            pointerEvents: "none",
          }}
        />
      </div>

      <div className="container py-5">
        <div className="row gy-4 align-items-start">
          {/* Brand / About */}
          <div className="col-lg-4">
            <div className="d-flex align-items-center gap-3 mb-3">
              <div
                aria-hidden="true"
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(135deg, rgba(13,110,253,0.18), rgba(11,59,145,0.22))",
                  border: "1px solid rgba(255,255,255,0.14)",
                  boxShadow: "0 18px 40px rgba(13,110,253,0.18)",
                  overflow: "hidden",
                }}
              >
                <img
                  src="/iblogo.png"
                  alt="IBG Logo"
                  width="28"
                  height="28"
                  style={{ display: "block", objectFit: "contain" }}
                  loading="lazy"
                />
              </div>

              <div className="lh-sm">
                <div
                  className="fw-bold"
                  style={{ letterSpacing: "-0.02em", fontSize: "1.05rem" }}
                >
                  Institute of Bankers of The Gambia
                </div>
                <div style={{ color: "rgba(255,255,255,0.72)", fontWeight: 700, fontSize: "0.9rem" }}>
                  Professional Banking Education & Development
                </div>
              </div>
            </div>

            <p style={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.85, marginBottom: 16 }}>
              Empowering banking professionals with certifications, diplomas, short courses, and capacity-building programmes
              designed for today’s financial world.
            </p>

            <div className="d-grid gap-2">
              <div
                className="d-flex align-items-start gap-2"
                style={{
                  padding: "0.75rem 0.9rem",
                  borderRadius: 18,
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.10)",
                }}
              >
                <FaMapMarkerAlt style={{ color: "var(--ibg-blue-light)", marginTop: 3 }} aria-hidden="true" />
                <div style={{ color: "rgba(255,255,255,0.80)" }}>
                  12 Kairaba Avenue, Serrekunda, The Gambia
                </div>
              </div>

              <a
                href="tel:+2202273987"
                className="text-decoration-none"
                aria-label="Phone number +220 2273987"
                style={{
                  padding: "0.75rem 0.9rem",
                  borderRadius: 18,
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  color: "rgba(255,255,255,0.88)",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <FaPhoneAlt style={{ color: "var(--ibg-blue-light)" }} aria-hidden="true" />
                <span>+220 2273987 / 2025336</span>
              </a>

              <a
                href="mailto:info@ibgambia.com"
                className="text-decoration-none"
                aria-label="Email info@ibgambia.com"
                style={{
                  padding: "0.75rem 0.9rem",
                  borderRadius: 18,
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  color: "rgba(255,255,255,0.88)",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <FaEnvelopeOpenText style={{ color: "var(--ibg-blue-light)" }} aria-hidden="true" />
                <span>info@ibgambia.com</span>
              </a>
            </div>
          </div>

          {/* Navigation */}
          <nav className="col-6 col-lg-2" aria-label="Footer navigation">
            <div
              className="text-uppercase fw-semibold mb-3"
              style={{
                color: "var(--ibg-blue-pale)",
                letterSpacing: "0.10em",
                fontSize: "0.78rem",
              }}
            >
              Explore
            </div>

            <ul className="list-unstyled m-0 d-grid gap-2">
              {footerNav.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="footer-link"
                    style={{
                      color: "rgba(255,255,255,0.82)",
                      textDecoration: "none",
                      fontWeight: 800,
                    }}
                    aria-label={`Go to ${item.label} page`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Newsletter */}
          <section className="col-lg-3" aria-labelledby="newsletter-heading">
            <div
              id="newsletter-heading"
              className="text-uppercase fw-semibold mb-3"
              style={{
                color: "var(--ibg-blue-pale)",
                letterSpacing: "0.10em",
                fontSize: "0.78rem",
              }}
            >
              Newsletter
            </div>

            <p style={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.85 }}>
              Get updates on programmes, workshops, and industry insights directly to your inbox.
            </p>

            <NewsletterForm source="footer" labelClassName="text-white" compact />
          </section>

          {/* Social + Portal CTA */}
          <section className="col-lg-3" aria-label="Social links and portal">
            <div
              className="text-uppercase fw-semibold mb-3"
              style={{
                color: "var(--ibg-blue-pale)",
                letterSpacing: "0.10em",
                fontSize: "0.78rem",
              }}
            >
              Connect
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3">
              {socialLinks.map(({ href, label, icon }) => (
                <a
                  key={label}
                  href={href}
                  className="social-pill"
                  target={href.startsWith("mailto:") ? undefined : "_blank"}
                  rel={href.startsWith("mailto:") ? undefined : "noreferrer"}
                  aria-label={label}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 44,
                    height: 44,
                    borderRadius: 16,
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    color: "rgba(255,255,255,0.85)",
                    textDecoration: "none",
                    transition: "transform 150ms ease, background 150ms ease, color 150ms ease",
                  }}
                >
                  {icon}
                </a>
              ))}
            </div>

            <div
              className="p-3"
              style={{
                borderRadius: 20,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.10)",
              }}
            >
              <div className="d-flex align-items-start justify-content-between gap-3">
                <div style={{ minWidth: 0 }}>
                  <div className="fw-bold" style={{ letterSpacing: "-0.02em" }}>
                    Student Portal
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>
                    Apply, upload documents, and track admissions status online.
                  </div>
                </div>
                <FaShieldAlt aria-hidden="true" style={{ color: "var(--ibg-blue-light)", marginTop: 2, flexShrink: 0 }} />
              </div>

              <motion.a
                href="https://portal.ibgambia.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn mt-3 w-100"
                whileHover={{ y: -2 }}
                whileTap={{ y: 0 }}
                aria-label="Open Student Portal"
                style={{
                  borderRadius: 16,
                  minHeight: 48,
                  fontWeight: 900,
                  color: "var(--ibg-navy)",
                  background: "linear-gradient(135deg, var(--ibg-white) 0%, var(--ibg-tint) 100%)",
                  border: "none",
                  boxShadow: "0 18px 40px rgba(0,0,0,0.14)",
                }}
              >
                Open Portal <FaArrowRight className="ms-2" />
              </motion.a>

              <div className="d-flex align-items-center gap-2 mt-2" style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.9rem" }}>
                <FaGlobe aria-hidden="true" />
                www.ibgambia.com
              </div>
            </div>
          </section>
        </div>

        <hr style={{ borderColor: "rgba(255,255,255,0.14)", margin: "2.5rem 0 1.25rem" }} />

        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-2">
          <div style={{ color: "rgba(255,255,255,0.72)", fontWeight: 700 }}>
            © {year} Institute of Bankers of The Gambia
          </div>

          <div className="d-flex align-items-center gap-2" style={{ color: "rgba(255,255,255,0.65)", fontWeight: 700 }}>
            <span>Built for professional banking excellence</span>
          </div>
        </div>
      </div>

      <style>{`
        .footer-link {
          transition: color 160ms ease, transform 160ms ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .footer-link:hover,
        .footer-link:focus {
          color: var(--ibg-blue-light) !important;
          transform: translateX(2px);
          text-decoration: none;
        }

        .social-pill:hover,
        .social-pill:focus {
          background: rgba(13,110,253,0.16) !important;
          color: var(--ibg-white) !important;
          transform: translateY(-2px);
          outline: none;
          box-shadow: 0 0 0 3px rgba(13,110,253,0.18);
        }

        .form-control:focus {
          border-color: rgba(13,110,253,0.35) !important;
          box-shadow: 0 0 0 3px rgba(13,110,253,0.12) !important;
        }

        /* Keep things clean on small screens */
        @media (max-width: 991.98px) {
          footer .container { padding-left: 1rem; padding-right: 1rem; }
        }
      `}</style>
    </footer>
  );
}

export default Footer;